<template>
  <BuildingPage title="报告列表" description="采用经典后台列表页结构，便于后续接入报告查询、分页、批量操作和导出能力。">
    <SearchPanel>
      <el-input v-model.trim="queryForm.keyword" placeholder="报告名称 / 编号" clearable></el-input>
      <el-select v-model="queryForm.reportType" clearable placeholder="报告类型" @change="handleSearch">
        <el-option v-for="item in reportTypeOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
      </el-select>
      <el-select v-model="queryForm.status" clearable placeholder="状态" @change="handleSearch">
        <el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
      </el-select>
      <el-date-picker
        v-model="queryForm.dateRange"
        type="daterange"
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        value-format="yyyy-MM-dd"
        @change="handleSearch"
      ></el-date-picker>
      <template #actions>
        <el-button @click="resetQuery">重置</el-button>
        <el-button type="primary" @click="handleSearch">查询</el-button>
      </template>
    </SearchPanel>

    <TablePanel title="报告台账" description="优先复用通用表格能力，先按后端返回字段完成列表渲染。">
      <template #extra>
        <el-button type="primary" icon="el-icon-plus" @click="goCreatePage">新建报告</el-button>
      </template>

      <aj-table
        v-loading="loading"
        :data="tableData"
        :columns="columns"
        :current-page.sync="queryForm.currentPage"
        :page-size.sync="queryForm.pageSize"
        :total="total"
        :page-sizes="[10, 20, 30, 50]"
        table-size="comfortable"
        :row-height="48"
        :cell-padding="10"
        row-id-key="id"
        use-internal-pagination
        height="360"
        stripe
        border
        @page-change="handlePageChange"
        @operate-btn-click="handleOperate"
      >
        <template #cell-statusName="{ row }">
          <span class="rm-tag" :class="row.tagClass">{{ row.statusName }}</span>
        </template>
      </aj-table>

      <EmptyState
        v-if="!loading && tableData.length === 0"
        title="暂无报告"
        description="当前筛选条件下没有匹配的报告数据。"
        icon="el-icon-document"
      ></EmptyState>
    </TablePanel>

    <aj-form-edit
      :dialog-visible.sync="editDialogVisible"
      :form-config="editFormConfig"
      :form-data="editFormData"
      dialog-title="编辑报告"
      dialog-width="80%"
      :reset-upload-on-cancel="false"
      @confirm="handleEditConfirm"
    />
  </BuildingPage>
</template>

<script>
import { getReportList, normalizePageResponse, updateReport } from '@/api/report'
import { uploadToCos } from '@/utils/cosUpload'
import AjFormEdit from '@/components/ajForm/ajFormEdit.vue'
import AjTable from '@/components/ajTable/ajTable.vue'
import EmptyState from '@/components/EmptyState.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'
import BuildingPage from '@/views/report/building/building.vue'

const REPORT_TYPE_OPTIONS = [
  { label: '经营分析', value: '经营分析' },
  { label: '项目复盘', value: '项目复盘' },
  { label: '服务质量', value: '服务质量' }
]

const STATUS_OPTIONS = [
  { label: '草稿', value: '草稿', tagClass: 'rm-tag--primary' },
  { label: '审核中', value: '审核中', tagClass: 'rm-tag--warning' },
  { label: '已归档', value: '已归档', tagClass: 'rm-tag--success' }
]

const FALLBACK_LIST = [
  { code: 'RP-202604-001', name: '一季度经营分析报告', type: '经营分析', owner: '陈静', status: '审核中', updatedAt: '2026-04-16 10:21' },
  { code: 'RP-202604-002', name: '客户服务质量月报', type: '服务质量', owner: '王敏', status: '草稿', updatedAt: '2026-04-15 18:05' },
  { code: 'RP-202604-003', name: '项目交付复盘报告', type: '项目复盘', owner: '李航', status: '已归档', updatedAt: '2026-04-14 16:40' }
]

function cloneEditData (row) {
  return {
    id: row.id,
    reportCode: row.reportCode,
    reportName: row.reportName,
    reportType: row.reportType,
    ownerName: row.ownerName,
    status: row.status,
    remark: row.remark,
    materials: Array.isArray(row.materials) ? row.materials.map(item => ({ ...item })) : []
  }
}

export default {
  name: 'ReportListPage',
  components: {
    AjFormEdit,
    AjTable,
    BuildingPage,
    EmptyState,
    SearchPanel,
    TablePanel
  },
  data () {
    return {
      loading: false,
      total: 0,
      uploadToCos,
      tableData: [],
      reportTypeOptions: REPORT_TYPE_OPTIONS,
      statusOptions: STATUS_OPTIONS,
      columns: [
        { type: 'index', label: '序号', width: 64 },
        { prop: 'reportCode', label: '报告编号', width: 150 },
        { prop: 'reportName', label: '报告名称', minWidth: 260, cellPadding: 10 },
        { prop: 'reportType', label: '类型', width: 120 },
        { prop: 'ownerName', label: '负责人', width: 110 },
        { prop: 'statusName', label: '状态', width: 112 },
        { prop: 'updatedAt', label: '更新时间', width: 172 },
        {
          type: 'operation',
          prop: 'operation',
          label: '操作',
          width: 168,
          fixed: 'right',
          buttons: [
            { text: '查看', code: 'view' },
            { text: '编辑', code: 'edit' },
            { text: '导出', code: 'export' }
          ]
        }
      ],
      queryForm: {
        keyword: '',
        reportType: '',
        status: '',
        dateRange: [],
        currentPage: 1,
        pageSize: 10
      },
      editDialogVisible: false,
      editFormData: {
        id: '',
        reportCode: '',
        reportName: '',
        reportType: '',
        ownerName: '',
        status: '',
        remark: '',
        materials: []
      }
    }
  },
  computed: {
    editFormConfig () {
      return [
        [
          { label: '报告编号', prop: 'reportCode', type: 'input', span: 12, disabled: true },
          { label: '报告名称', prop: 'reportName', type: 'input', span: 12, required: true }
        ],
        [
          { label: '报告类型', prop: 'reportType', type: 'select', span: 12, required: true, options: this.reportTypeOptions },
          { label: '负责人', prop: 'ownerName', type: 'input', span: 12, required: true }
        ],
        [
          { label: '状态', prop: 'status', type: 'select', span: 12, options: this.statusOptions.map(item => ({ label: item.label, value: item.value })) }
        ],
        [
          { label: '说明', prop: 'remark', type: 'textarea', span: 24, rows: 4 }
        ],
        [
          {
            label: '材料附件',
            prop: 'materials',
            type: 'upload',
            span: 24,
            multiple: true,
            limit: 10,
            acceptType: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.png,.jpg,.jpeg',
            uploadRequest: this.uploadToCos
          }
        ]
      ]
    }
  },
  created () {
    this.fetchList()
  },
  methods: {
    buildQueryParams () {
      return {
        keyword: this.queryForm.keyword || undefined,
        reportType: this.queryForm.reportType || undefined,
        status: this.queryForm.status || undefined,
        startTime: this.queryForm.dateRange && this.queryForm.dateRange[0] ? this.queryForm.dateRange[0] : undefined,
        endTime: this.queryForm.dateRange && this.queryForm.dateRange[1] ? this.queryForm.dateRange[1] : undefined,
        currentPage: this.queryForm.currentPage,
        pageSize: this.queryForm.pageSize
      }
    },
    normalizeStatus (status) {
      const target = this.statusOptions.find(item => item.value === status || item.label === status)
      return {
        status: target ? target.value : (status || '草稿'),
        statusName: target ? target.label : (status || '草稿'),
        tagClass: target ? target.tagClass : 'rm-tag--primary'
      }
    },
    normalizeReportRow (row, index) {
      const code = row.reportCode || row.code || row.ctid || `TEMP-${index + 1}`
      const name = row.reportName || row.name || row.ctname || '未命名报告'
      const type = row.reportType || row.type || '未分类'
      const owner = row.ownerName || row.owner || row.createusername || '未分配'
      const updatedAt = row.updatedAt || row.updateTime || row.updatetime || row.createtime || '-'
      const statusInfo = this.normalizeStatus(row.statusName || row.status || row.reportStatus)
      return {
        id: row.reportId || row.id || row.ctid || `${code}_${index}`,
        reportCode: code,
        reportName: name,
        reportType: type,
        ownerName: owner,
        updatedAt,
        status: statusInfo.status,
        statusName: statusInfo.statusName,
        tagClass: statusInfo.tagClass,
        remark: row.remark || row.description || '',
        materials: Array.isArray(row.materials) ? row.materials : []
      }
    },
    async fetchList () {
      this.loading = true
      try {
        const res = await getReportList(this.buildQueryParams())
        const pageData = normalizePageResponse(res)
        this.tableData = pageData.list.map((item, index) => this.normalizeReportRow(item, index))
        this.total = pageData.total
      } catch (error) {
        // 报表真实接口尚未在后端仓库中确认，开发阶段先回退到静态数据，保证页面可联调。
        this.tableData = FALLBACK_LIST.map((item, index) => this.normalizeReportRow(item, index))
        this.total = this.tableData.length
      } finally {
        this.loading = false
      }
    },
    handleSearch () {
      this.queryForm.currentPage = 1
      this.fetchList()
    },
    handlePageChange ({ currentPage, pageSize }) {
      this.queryForm.currentPage = currentPage
      this.queryForm.pageSize = pageSize
      this.fetchList()
    },
    resetQuery () {
      this.queryForm = {
        keyword: '',
        reportType: '',
        status: '',
        dateRange: [],
        currentPage: 1,
        pageSize: 10
      }
      this.fetchList()
    },
    goCreatePage () {
      this.$router.push('/report/create')
    },
    handleOperate ({ btn, row }) {
      if (btn.code === 'edit') {
        this.editFormData = cloneEditData(row)
        this.editDialogVisible = true
        return
      }
      if (btn.code === 'view') {
        this.$message.info(`当前先保留查看入口：${row.reportName}`)
        return
      }
      if (btn.code === 'export') {
        this.$message.info(`当前先保留导出入口：${row.reportName}`)
      }
    },
    async handleEditConfirm ({ data }) {
      try {
        await updateReport(data)
        this.$message.success('报告信息更新成功')
        this.editDialogVisible = false
        this.fetchList()
      } catch (error) {
        this.$message.warning('后端编辑接口暂未确认，当前已保留编辑弹框结构')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.rm-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 1;
}

.rm-tag--primary {
  color: #2f6bff;
  background: rgba(47, 107, 255, 0.12);
}

.rm-tag--warning {
  color: #c98512;
  background: rgba(201, 133, 18, 0.12);
}

.rm-tag--success {
  color: #1f8b4c;
  background: rgba(31, 139, 76, 0.12);
}
</style>
